import { useState, useEffect } from 'react';
import axios from 'axios';
import { Airplane, CreditCard, FirstAid, Phone, Sun, Thermometer, Translate, Warning, Bus, Car, MapTrifold, CalendarBlank } from '@phosphor-icons/react';

const API = '/api';

const defaultTravelTips = [
  {
    icon: <Airplane size={24} weight="light" />,
    title: 'Getting There',
    title_es: 'Cómo Llegar',
    content: 'Fly into Monseñor Óscar Arnulfo Romero International Airport (SAL) in San Salvador. Major airlines including United, American, Delta, and Avianca offer direct flights from US cities. Lake Coatapeque is approximately 1.5 hours from the airport.',
    content_es: 'Vuela al Aeropuerto Internacional Monseñor Óscar Arnulfo Romero (SAL) en San Salvador. Aerolíneas principales como United, American, Delta y Avianca ofrecen vuelos directos desde ciudades de EE.UU. El Lago de Coatepeque está aproximadamente a 1.5 horas del aeropuerto.',
  },
  {
    icon: <Thermometer size={24} weight="light" />,
    title: 'Weather',
    title_es: 'Clima',
    content: 'December is part of the dry season in El Salvador. Expect pleasant temperatures between 70-85°F (21-29°C) during the day, with cooler evenings around 60-65°F (15-18°C). Pack layers for the evening celebrations.',
    content_es: 'Diciembre es parte de la temporada seca en El Salvador. Espera temperaturas agradables entre 21-29°C durante el día, con noches más frescas alrededor de 15-18°C. Trae capas para las celebraciones nocturnas.',
  },
  {
    icon: <Translate size={24} weight="light" />,
    title: 'Language',
    title_es: 'Idioma',
    content: 'Spanish is the official language. English is spoken at major hotels and tourist areas, but learning a few Spanish phrases will be appreciated by locals. Consider downloading a translation app.',
    content_es: 'El español es el idioma oficial. El inglés se habla en los principales hoteles y zonas turísticas, pero aprender algunas frases en español será apreciado por los locales. Considera descargar una aplicación de traducción.',
  },
  {
    icon: <CreditCard size={24} weight="light" />,
    title: 'Currency',
    title_es: 'Moneda',
    content: 'El Salvador uses the US Dollar ($), making transactions easy for American visitors. ATMs are widely available. Credit cards are accepted at hotels and larger establishments, but carry some cash for smaller vendors.',
    content_es: 'El Salvador usa el Dólar estadounidense ($), lo que facilita las transacciones para los visitantes americanos. Los cajeros automáticos están ampliamente disponibles. Se aceptan tarjetas de crédito en hoteles y establecimientos grandes, pero lleva efectivo para vendedores pequeños.',
  },
  {
    icon: <FirstAid size={24} weight="light" />,
    title: 'Health & Safety',
    title_es: 'Salud y Seguridad',
    content: 'No vaccinations are required, but consult your doctor before travel. Drink bottled water to be safe. The wedding venues and hotels maintain high standards of safety and hygiene.',
    content_es: 'No se requieren vacunas, pero consulta a tu médico antes de viajar. Bebe agua embotellada para estar seguro. Los lugares de la boda y hoteles mantienen altos estándares de seguridad e higiene.',
  },
  {
    icon: <Phone size={24} weight="light" />,
    title: 'Connectivity',
    title_es: 'Conectividad',
    content: 'WiFi is available at most hotels. Consider purchasing a local SIM card or an international phone plan. WhatsApp is the most popular messaging app in El Salvador.',
    content_es: 'WiFi está disponible en la mayoría de los hoteles. Considera comprar una tarjeta SIM local o un plan de teléfono internacional. WhatsApp es la aplicación de mensajería más popular en El Salvador.',
  },
];

const defaultPackingList = [
  { en: 'Valid passport (check expiration date)', es: 'Pasaporte válido (verifica la fecha de vencimiento)' },
  { en: 'Comfortable walking shoes', es: 'Zapatos cómodos para caminar' },
  { en: 'Light layers for evening', es: 'Capas ligeras para la noche' },
  { en: 'Sunscreen and sunglasses', es: 'Protector solar y gafas de sol' },
  { en: 'Formal attire for the wedding', es: 'Atuendo formal para la boda' },
  { en: 'Casual clothes for activities', es: 'Ropa casual para actividades' },
  { en: 'Swimsuit for the lake', es: 'Traje de baño para el lago' },
  { en: 'Camera', es: 'Cámara' },
  { en: 'Any necessary medications', es: 'Medicamentos necesarios' },
  { en: 'Power adapter (same as US)', es: 'Adaptador de corriente (igual que EE.UU.)' },
];

const localAttractions = [
  {
    name: 'Lake Coatapeque',
    name_es: 'Lago de Coatepeque',
    description: 'A stunning volcanic crater lake perfect for kayaking, swimming, and sunset views.',
    description_es: 'Un impresionante lago de cráter volcánico perfecto para kayak, natación y vistas del atardecer.',
  },
  {
    name: 'Ruta de las Flores',
    name_es: 'Ruta de las Flores',
    description: 'A scenic route through charming mountain villages known for coffee, crafts, and flowers.',
    description_es: 'Una ruta escénica a través de encantadores pueblos de montaña conocidos por el café, artesanías y flores.',
  },
  {
    name: 'Santa Ana Volcano',
    name_es: 'Volcán de Santa Ana',
    description: 'Hike to the top of El Salvador\'s highest volcano for breathtaking views.',
    description_es: 'Camina hasta la cima del volcán más alto de El Salvador para vistas impresionantes.',
  },
  {
    name: 'Joya de Cerén',
    name_es: 'Joya de Cerén',
    description: 'UNESCO World Heritage Site - an ancient Mayan village preserved by volcanic ash.',
    description_es: 'Sitio del Patrimonio Mundial de la UNESCO - un antiguo pueblo maya preservado por ceniza volcánica.',
  },
  {
    name: 'El Tunco Beach',
    name_es: 'Playa El Tunco',
    description: 'A laid-back beach town famous for surfing and incredible sunsets.',
    description_es: 'Un pueblo playero relajado famoso por el surf y atardeceres increíbles.',
  },
];

const defaultContent = {
  airport_code: 'SAL',
  airport_name: 'Monseñor Óscar Arnulfo Romero International Airport',
  intro: '',
  intro_es: '',
  tips: '',
  tips_es: '',
  packing_list: '',
  packing_list_es: ''
};

export default function TravelPage() {
  const [content, setContent] = useState(defaultContent);
  const [travelTips, setTravelTips] = useState(defaultTravelTips);
  const [packingList, setPackingList] = useState(defaultPackingList);
  const [attractions, setAttractions] = useState(localAttractions);
  const [transportBullets, setTransportBullets] = useState([
    { en: 'We will be providing transportation to and from the airport for guests staying in the recommended areas.', es: 'Proporcionaremos transporte desde y hacia el aeropuerto para los invitados que se hospeden en las zonas recomendadas.' },
    { en: 'Shuttle transportation will also be provided between the recommended hotels/Airbnbs and the wedding venue throughout the weekend.', es: 'También habrá transporte entre los hoteles/Airbnbs recomendados y el lugar de la boda durante el fin de semana del evento.' },
    { en: 'If guests would prefer to arrange private drivers or additional transportation, we are happy to help organize and coordinate this.', es: 'Si algún invitado prefiere contratar un conductor privado u organizar transporte adicional, con gusto podemos ayudar a coordinarlo.' },
    { en: 'Please fill out your flight information in the Travel Info section of the website so we can properly plan transportation for everyone.', es: 'Por favor completen su información de vuelo en la sección "Travel Info" del sitio web para que podamos planificar el transporte adecuadamente.' },
  ]);
  const [tourInfo, setTourInfo] = useState({
    pdf_url: 'https://customer-assets.emergentagent.com/job_f5516538-c661-45b1-972e-9b2cbd4e0385/artifacts/k6bkk2z1_Cata%CC%81logo%20Tours%20DBT%20-%20Wedding%20%26%20Events%20SV.pdf',
    description: "We've partnered with Don Bosco Travel to offer amazing tour experiences throughout El Salvador. Special rates available for groups of 10 or more!",
    description_es: 'Nos hemos asociado con Don Bosco Travel para ofrecer increíbles experiencias de tour por El Salvador. ¡Tarifas especiales disponibles para grupos de 10 o más!',
    phone: '+50378205478',
    email: 'donboscotravel@gmail.com',
    instagram: '@donboscotravel',
  });

  const [weekendEvents, setWeekendEvents] = useState([]);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await axios.get(`${API}/content/travel`);
        if (response.data) {
          setContent(prev => ({ ...prev, ...response.data }));
          if (response.data.transportation_bullets?.length) setTransportBullets(response.data.transportation_bullets);
          if (response.data.travel_tips?.length) {
            setTravelTips(response.data.travel_tips.map(t => ({
              ...t,
              icon: defaultTravelTips.find(d => d.title === t.title)?.icon || <Sun size={24} weight="light" />,
            })));
          }
          if (response.data.packing_list_items?.length) setPackingList(response.data.packing_list_items);
          if (response.data.local_attractions?.length) setAttractions(response.data.local_attractions);
          if (response.data.wedding_weekend_events?.length) setWeekendEvents(response.data.wedding_weekend_events);
          if (response.data.tour_pdf_url) setTourInfo(prev => ({
            ...prev,
            pdf_url: response.data.tour_pdf_url || prev.pdf_url,
            description: response.data.tour_description || prev.description,
            description_es: response.data.tour_description_es || prev.description_es,
            phone: response.data.tour_phone || prev.phone,
            email: response.data.tour_email || prev.email,
            instagram: response.data.tour_instagram || prev.instagram,
          }));
        }
      } catch (error) {
        console.log('Using default travel content');
      }
    };
    fetchContent();
  }, []);

  return (
    <div data-testid="travel-page" className="min-h-screen bg-stone-50">
      {/* Hero Header */}
      <div
        className="relative py-32 md:py-48"
        style={{
          backgroundImage: 'url(https://customer-assets.emergentagent.com/job_coatapeque-wedding/artifacts/mhrffcit_IMG_6086.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative z-10 text-center text-white px-4">
          <p className="font-body text-sm tracking-[0.2em] uppercase mb-4" style={{ color: "#C9B99A" }}>
            Your Guide to
          </p>
          <p className="font-body text-xs italic mb-2 text-white/70">
            Tu Guía de
          </p>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight">
            El Salvador
          </h1>
        </div>
      </div>

      {/* Welcome Section */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="md:w-1/2">
              <img
                src="https://customer-assets.emergentagent.com/job_coatapeque-wedding/artifacts/f5y8f5ka_ChatGPT%20Image%20Jan%2020%2C%202026%2C%2012_51_33%20PM.png"
                alt="Greetings from El Salvador postcard"
                className="w-full max-w-md mx-auto shadow-lg border-4 border-white"
                style={{ transform: 'rotate(-2deg)' }}
              />
            </div>
            <div className="md:w-1/2 text-center md:text-left">
              <h2 className="font-display text-2xl md:text-3xl text-stone-900 mb-2">
                Welcome to El Salvador!
              </h2>
              <p className="font-body text-sm italic text-stone-400 mb-4">
                ¡Bienvenidos a El Salvador!
              </p>
              {content.intro ? (
                <>
                  {content.intro.split('\n\n').map((para, i) => (
                    <p key={i} className="font-body text-stone-600 leading-relaxed mb-3">{para}</p>
                  ))}
                  {content.intro_es && content.intro_es.split('\n\n').map((para, i) => (
                    <p key={`es-${i}`} className="font-body text-sm italic text-stone-400 leading-relaxed mb-2">{para}</p>
                  ))}
                </>
              ) : (
                <>
                  <p className="font-body text-stone-600 leading-relaxed">
                    Known as the "Land of Volcanoes," El Salvador is a hidden gem of Central America.
                    From pristine Pacific beaches and world-class surfing to stunning volcanic lakes
                    and rich Mayan history, there's so much to explore during your visit.
                  </p>
                  <p className="font-body text-sm italic text-stone-400 mt-3 leading-relaxed">
                    Conocido como la "Tierra de Volcanes," El Salvador es una joya escondida de Centroamérica.
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* TRANSPORTATION SECTION */}
      <section data-testid="transportation-section" className="py-16 md:py-24 bg-[#FAF8F5] border-y border-[#E8E2D9]">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#C9B99A] rounded-full mb-6">
              <Bus size={32} weight="light" className="text-white" />
            </div>
            <h2 className="font-display text-3xl md:text-4xl text-stone-900 mb-2">
              Transportation
            </h2>
            <p className="font-body text-lg italic text-stone-400 mb-6">
              Transporte
            </p>
          </div>

          <div className="bg-white border border-stone-200 p-8 md:p-10">
            {/* Intro note */}
            <p className="font-body text-stone-700 leading-relaxed text-center mb-3">
              We are planning to provide transportation for guests throughout the wedding weekend, including transportation from the airport to San Salvador hotels.
            </p>
            <p className="font-body text-sm italic text-stone-400 text-center mb-10">
              Estamos planeando proporcionar transporte para los invitados durante todo el fin de semana de la boda, incluyendo transporte desde el aeropuerto a los hoteles en San Salvador.
            </p>

            {/* Day-by-day event transport */}
            <div className="space-y-8">
              {weekendEvents.map((event, idx) => (
                <div key={idx} className={`${idx > 0 ? 'border-t border-stone-200 pt-8' : ''}`}>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#C9B99A] rounded-full flex items-center justify-center flex-shrink-0 text-white font-display text-sm">
                      {idx + 1}
                    </div>
                    <div className="flex-1">
                      <p className="font-body text-xs tracking-[0.15em] uppercase text-[#B8A88A] mb-1">{event.day}</p>
                      <p className="font-body text-[10px] italic text-stone-400 mb-2">{event.day_es}</p>
                      <h3 className="font-display text-xl text-stone-900 mb-1">{event.title}</h3>
                      <p className="font-body text-sm italic text-stone-400 mb-3">{event.title_es}</p>
                      <p className="font-body text-stone-600 leading-relaxed">{event.note}</p>
                      <p className="font-body text-sm italic text-stone-400 mt-1">{event.note_es}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Closing note */}
            <div className="border-t border-stone-200 mt-8 pt-8 text-center">
              <div className="bg-[#FAF8F5] border border-[#C9B99A] rounded p-5 mb-4">
                <p className="font-body text-stone-700 font-medium leading-relaxed">
                  Please fill out your travel details on the <a href="/flights" className="text-[#B8A88A] underline hover:text-[#9A8A6A] font-semibold">Travel Info</a> page to help us organize transportation for everyone!
                </p>
                <p className="font-body text-sm italic text-stone-400 mt-2">
                  Por favor completa tu información de viaje en la página de <a href="/flights" className="text-[#B8A88A] underline hover:text-[#9A8A6A]">Info de Viaje</a> para ayudarnos a organizar el transporte para todos.
                </p>
              </div>
              <p className="font-body text-stone-500 text-sm leading-relaxed">
                More transportation details and timing will be shared as we get closer to the celebration.
              </p>
              <p className="font-body text-xs italic text-stone-400 mt-1">
                Más detalles de transporte y horarios se compartirán a medida que se acerque la celebración.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* TOUR OPTIONS SECTION */}
      <section data-testid="tour-options-section" className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <p className="font-body text-sm tracking-[0.2em] uppercase mb-4" style={{ color: "#B8A88A" }}>
              Explore El Salvador
            </p>
            <p className="font-body text-xs italic text-stone-400 -mt-2 mb-4">
              Explora El Salvador
            </p>
            <h2 className="font-display text-3xl md:text-4xl text-stone-900 mb-2">
              Tour Options
            </h2>
            <p className="font-body text-lg italic text-stone-400 mb-6">
              Opciones de Tours
            </p>
            <p className="font-body text-stone-600 max-w-2xl mx-auto">
              {tourInfo.description}
            </p>
            <p className="font-body text-sm italic text-stone-400 mt-2 max-w-2xl mx-auto">
              {tourInfo.description_es}
            </p>
          </div>

          {/* PDF Download Card */}
          <div className="bg-[#FAF8F5] border border-[#E8E2D9] p-8 md:p-12 text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-[#C9B99A] rounded-full mb-6">
              <MapTrifold size={40} weight="light" className="text-white" />
            </div>
            
            <h3 className="font-display text-2xl text-stone-900 mb-2">
              Tour Catalog
            </h3>
            <p className="font-body text-sm italic text-stone-400 mb-4">
              Catálogo de Tours
            </p>
            
            <p className="font-body text-stone-600 mb-6 max-w-lg mx-auto">
              Download the complete tour catalog featuring 9 unique experiences including volcano hikes, 
              colonial towns, coffee tours, hot springs, beaches, and archaeological sites.
            </p>
            <p className="font-body text-sm italic text-stone-400 mb-8 max-w-lg mx-auto">
              Descarga el catálogo completo de tours con 9 experiencias únicas incluyendo caminatas a volcanes, 
              pueblos coloniales, tours de café, aguas termales, playas y sitios arqueológicos.
            </p>

            <a 
              href={tourInfo.pdf_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-8 py-4 bg-[#C9B99A] text-white font-body tracking-wide hover:bg-[#B8A88A] transition-colors"
              data-testid="download-tour-catalog"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 256 256">
                <path d="M224,152v56a16,16,0,0,1-16,16H48a16,16,0,0,1-16-16V152a8,8,0,0,1,16,0v56H208V152a8,8,0,0,1,16,0Zm-101.66,5.66a8,8,0,0,0,11.32,0l40-40a8,8,0,0,0-11.32-11.32L136,132.69V40a8,8,0,0,0-16,0v92.69L93.66,106.34a8,8,0,0,0-11.32,11.32Z"></path>
              </svg>
              View / Download Catalog
            </a>
            <p className="font-body text-xs text-stone-400 mt-3">
              Ver / Descargar Catálogo (PDF)
            </p>
          </div>

          {/* Contact Info */}
          <div className="mt-8 text-center p-8 bg-white border border-stone-200">
            <h3 className="font-display text-xl text-stone-900 mb-2">
              Book Your Tours
            </h3>
            <p className="font-body text-sm italic text-stone-400 mb-4">
              Reserva tus Tours
            </p>
            <p className="font-body text-stone-600 mb-4">
              Contact Don Bosco Travel directly to book. Mention our wedding for group coordination!
            </p>
            <p className="font-body text-sm italic text-stone-400 mb-6">
              Contacta a Don Bosco Travel directamente. ¡Menciona nuestra boda para coordinación de grupo!
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              {tourInfo.phone && (
              <a 
                href={`tel:${tourInfo.phone}`}
                className="flex items-center gap-2 text-[#B8A88A] hover:text-[#9A8A6A] transition-colors"
              >
                <Phone size={18} />
                <span className="font-body">{tourInfo.phone}</span>
              </a>
              )}
              {tourInfo.email && (
              <a 
                href={`mailto:${tourInfo.email}`}
                className="flex items-center gap-2 text-[#B8A88A] hover:text-[#9A8A6A] transition-colors"
              >
                <span className="font-body">{tourInfo.email}</span>
              </a>
              )}
              {tourInfo.instagram && (
              <a 
                href={`https://instagram.com/${tourInfo.instagram.replace('@', '')}`}
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-[#B8A88A] hover:text-[#9A8A6A] transition-colors"
              >
                <span className="font-body">{tourInfo.instagram}</span>
              </a>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Travel Tips */}
      <section data-testid="travel-tips-section" className="py-16 md:py-24 bg-stone-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <div className="decorative-line mx-auto mb-8" />
            <h2 className="font-display text-3xl md:text-4xl text-stone-900">
              Travel Tips
            </h2>
            <p className="font-body text-lg italic text-stone-400 mt-2">
              Consejos de Viaje
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {travelTips.map((tip, index) => (
              <div key={index} className="p-6 bg-stone-50 border border-stone-200">
                <div className="text-[#B8A88A] mb-4">{tip.icon}</div>
                <h3 className="font-display text-xl text-stone-900 mb-1">
                  {tip.title}
                </h3>
                <p className="font-body text-sm italic text-stone-400 mb-3">
                  {tip.title_es}
                </p>
                <p className="font-body text-sm text-stone-600 leading-relaxed mb-3">
                  {tip.content}
                </p>
                <p className="font-body text-xs italic text-stone-400 leading-relaxed">
                  {tip.content_es}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Packing List */}
      <section data-testid="packing-section" className="py-16 md:py-24 bg-stone-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start">
            <div>
              <p className="font-body text-sm tracking-[0.2em] uppercase mb-4" style={{ color: "#B8A88A" }}>
                Don't Forget
              </p>
              <p className="font-body text-xs italic text-stone-400 -mt-2 mb-4">
                No Olvides
              </p>
              <h2 className="font-display text-3xl text-stone-900 mb-2">
                Packing Essentials
              </h2>
              <p className="font-body text-lg italic text-stone-400 mb-6">
                Esenciales para Empacar
              </p>
              <p className="font-body text-stone-600 leading-relaxed">
                Here are some essentials to pack for your trip to El Salvador. 
                The wedding will be a mix of elegant evening events and casual 
                daytime activities, so pack accordingly.
              </p>
              <p className="font-body text-sm italic text-stone-400 mt-3 leading-relaxed">
                Aquí hay algunos esenciales para empacar para tu viaje a El Salvador. 
                La boda será una mezcla de eventos elegantes de noche y actividades 
                casuales de día, así que empaca en consecuencia.
              </p>
            </div>

            <div className="bg-white border border-stone-200 p-8">
              <ul className="space-y-4">
                {packingList.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-[#C9B99A] rounded-full flex-shrink-0 mt-2" />
                    <div>
                      <p className="font-body text-stone-700">{item.en}</p>
                      <p className="font-body text-xs italic text-stone-400">{item.es}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Local Attractions */}
      <section data-testid="attractions-section" className="py-16 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <p className="font-body text-sm tracking-[0.2em] uppercase mb-4" style={{ color: "#B8A88A" }}>
              Explore
            </p>
            <p className="font-body text-xs italic text-stone-400 -mt-2 mb-4">
              Explora
            </p>
            <h2 className="font-display text-3xl md:text-4xl text-stone-900">
              Local Attractions
            </h2>
            <p className="font-body text-lg italic text-stone-400 mt-2">
              Atracciones Locales
            </p>
          </div>

          {/* San Salvador Historic Center group */}
          {attractions.some(a => ['Metropolitan Cathedral', 'National Palace', 'National Theater', 'National Library (BINAES)', 'El Rosario Church'].includes(a.name) || a.name.includes('Plaza')) && (
            <div className="mb-16">
              <div className="text-center mb-8">
                <h3 className="font-display text-2xl text-stone-900">Explore San Salvador</h3>
                <p className="font-body text-sm italic text-stone-400 mt-1">Explora San Salvador</p>
                <p className="font-body text-stone-500 mt-3 max-w-2xl mx-auto text-sm">
                  San Salvador's Historic Center is a great place to explore the city's culture, architecture, history, and local energy. Many of the main attractions are close to each other, making it easy to visit several spots in one trip.
                </p>
                <p className="font-body text-xs italic text-stone-400 mt-2 max-w-2xl mx-auto">
                  El Centro Histórico de San Salvador es un gran lugar para explorar la cultura, arquitectura, historia y energía local de la ciudad. Muchas de las atracciones principales están cerca una de la otra, lo que facilita visitar varios lugares en un solo viaje.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {attractions.filter(a => 
                  ['Metropolitan Cathedral', 'National Palace', 'National Theater', 'National Library (BINAES)', 'El Rosario Church', 'Local Restaurants & Cafés'].includes(a.name) || a.name.includes('Plaza')
                ).map((attraction, index) => (
                  <div key={index} className="p-5 bg-[#FAF8F5] border border-[#E8E2D9] rounded">
                    <h4 className="font-display text-lg text-stone-900">
                      {attraction.name}
                      {attraction.name_es !== attraction.name && (
                        <span className="font-body text-xs italic text-stone-400 ml-2">/ {attraction.name_es}</span>
                      )}
                    </h4>
                    <p className="font-body text-sm text-stone-600 mt-2 leading-relaxed">{attraction.description}</p>
                    <p className="font-body text-xs italic text-stone-400 mt-1">{attraction.description_es}</p>
                  </div>
                ))}
              </div>
              {/* Suggested Plan */}
              <div className="mt-6 p-5 bg-white border-2 border-[#C9B99A] rounded text-center">
                <p className="font-body text-sm font-medium text-stone-700">
                  <strong>Suggested Plan:</strong> We recommend visiting the Historic Center during the day or early evening. Walk through the plazas, visit the Cathedral, National Palace, National Theater, BINAES, and El Rosario Church, then enjoy coffee or dinner nearby.
                </p>
                <p className="font-body text-xs italic text-stone-400 mt-2">
                  <strong>Plan Sugerido:</strong> Recomendamos visitar el Centro Histórico durante el día o al atardecer. Pasea por las plazas, visita la Catedral, el Palacio Nacional, el Teatro Nacional, BINAES y la Iglesia El Rosario, y luego disfruta de café o cena cerca.
                </p>
              </div>
            </div>
          )}

          {/* Beyond San Salvador */}
          {attractions.some(a => ['Lake Coatapeque', 'Ruta de las Flores', 'Santa Ana Volcano', 'Joya de Cerén', 'El Tunco Beach'].includes(a.name)) && (
            <div>
              <div className="text-center mb-8">
                <h3 className="font-display text-2xl text-stone-900">Beyond San Salvador</h3>
                <p className="font-body text-sm italic text-stone-400 mt-1">Más Allá de San Salvador</p>
                <p className="font-body text-stone-500 mt-3 max-w-xl mx-auto text-sm">
                  Extend your trip and explore the beauty of El Salvador
                </p>
                <p className="font-body text-xs italic text-stone-400 mt-1">
                  Extiende tu viaje y explora la belleza de El Salvador
                </p>
              </div>
              <div className="space-y-4">
                {attractions.filter(a => 
                  ['Lake Coatapeque', 'Ruta de las Flores', 'Santa Ana Volcano', 'Joya de Cerén', 'El Tunco Beach'].includes(a.name)
                ).map((attraction, index) => (
                  <div key={index} className="p-6 bg-stone-50 border border-stone-200 flex flex-col md:flex-row md:items-center gap-4">
                    <div className="flex items-center justify-center w-12 h-12 bg-[#C9B99A] text-white font-display text-xl flex-shrink-0 rounded-full">
                      {index + 1}
                    </div>
                    <div>
                      <h4 className="font-display text-xl text-stone-900">
                        {attraction.name}
                        {attraction.name_es !== attraction.name && (
                          <span className="font-body text-sm italic text-stone-400 ml-2">/ {attraction.name_es}</span>
                        )}
                      </h4>
                      <p className="font-body text-sm text-stone-600 mt-1">{attraction.description}</p>
                      <p className="font-body text-xs italic text-stone-400 mt-1">{attraction.description_es}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Important Notice */}
      <section data-testid="notice-section" className="py-12 bg-[#F5F2ED] border-y border-[#E8E2D9]">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-start gap-4">
            <Warning size={24} style={{ color: "#B8A88A" }} className="flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-display text-lg text-stone-900 mb-1">
                Travel Advisory
              </h3>
              <p className="font-body text-sm italic text-stone-400 mb-3">
                Aviso de Viaje
              </p>
              <p className="font-body text-sm text-stone-700 leading-relaxed">
                Please check current travel advisories before your trip and ensure your passport is valid 
                for at least 6 months beyond your travel dates. We recommend purchasing travel insurance 
                for your peace of mind. If you have any concerns, please reach out to us directly.
              </p>
              <p className="font-body text-xs italic text-stone-500 mt-3 leading-relaxed">
                Por favor verifica los avisos de viaje actuales antes de tu viaje y asegúrate de que tu pasaporte 
                sea válido por al menos 6 meses después de tus fechas de viaje. Recomendamos comprar un seguro de viaje 
                para tu tranquilidad. Si tienes alguna pregunta, no dudes en contactarnos directamente.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
